import { Component, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RoleFormatPipe } from '../../shared/pipes/role-format.pipe';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { UserService } from '../../core/services/user.service';
import { RoleService } from '../../core/services/role.service';
import { Role } from '../../core/models/models';

@Component({
  selector: 'app-user-create',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    RoleFormatPipe
  ],
  template: `
    <div class="page-header">
      <div class="title-section">
        <h2 class="page-title">Nouvel utilisateur</h2>
        <p class="page-subtitle">Remplissez les informations pour créer un nouveau compte</p>
      </div>
      <a routerLink="/users" mat-stroked-button class="back-link">
        <mat-icon>arrow_back</mat-icon>
        Retour à la liste
      </a>
    </div>

    <div class="form-container">
      <div class="card shadow-sm">
        <form [formGroup]="userForm" (ngSubmit)="onSubmit()" class="modern-form">
          <div class="form-section">
            <h3 class="section-title">Informations de connexion</h3>
            <div class="form-row">
              <mat-form-field appearance="outline" class="col">
                <mat-label>NOM D'UTILISATEUR</mat-label>
                <input matInput id="username" formControlName="username" placeholder="ex: jdupont">
              </mat-form-field>
              <mat-form-field appearance="outline" class="col">
                <mat-label>ADRESSE EMAIL</mat-label>
                <input matInput type="email" id="email" formControlName="email" placeholder="ex: jean.dupont&#64;avikeo.com">
              </mat-form-field>
            </div>
            <div class="form-row">
              <mat-form-field appearance="outline" class="col">
                <mat-label>MOT DE PASSE</mat-label>
                <input matInput type="password" id="password" formControlName="password" placeholder="Minimum 6 caractères">
              </mat-form-field>
              <mat-form-field appearance="outline" class="col">
                <mat-label>RÔLE</mat-label>
                <mat-select id="role" formControlName="role">
                  @for (role of roles(); track role.id) {
                    <mat-option [value]="role.name">{{ role | roleFormat }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <div class="form-section">
            <h3 class="section-title">Informations personnelles</h3>
            <div class="form-row">
              <mat-form-field appearance="outline" class="col">
                <mat-label>PRÉNOM</mat-label>
                <input matInput id="firstName" formControlName="firstName" placeholder="ex: Jean">
              </mat-form-field>
              <mat-form-field appearance="outline" class="col">
                <mat-label>NOM</mat-label>
                <input matInput id="lastName" formControlName="lastName" placeholder="ex: Dupont">
              </mat-form-field>
            </div>
          </div>

          <div class="form-section">
            <mat-checkbox formControlName="accessEnabled" color="primary">
              Activer l'accès au compte dès la création
            </mat-checkbox>
          </div>

          @if (errorMessage()) {
            <div class="error-banner">
              <mat-icon color="error">error</mat-icon>
              {{ errorMessage() }}
            </div>
          }

          <div class="form-actions">
            <button type="button" mat-button routerLink="/users">Annuler</button>
            <button type="submit" mat-raised-button color="primary" [disabled]="userForm.invalid || isLoading()">
              @if (isLoading()) { <mat-icon class="spin" matButtonIcon>sync</mat-icon> }
              @if (isLoading()) { <span>Création en cours...</span> } @else { <span>Créer l'utilisateur</span> }
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
  styles: [`
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-bottom: 2rem;
    }
    .page-title {
      margin: 0;
      font-size: 1.5rem;
      color: #1a237e;
    }
    .page-subtitle {
      margin: 0.25rem 0 0;
      color: var(--text-secondary);
      font-size: 0.9rem;
    }
    .form-container {
      max-width: 800px;
    }
    .modern-form {
      padding: 1rem;
    }
    .form-section {
      margin-bottom: 2.5rem;
    }
    .section-title {
      font-size: 1rem;
      font-weight: 700;
      color: var(--primary-color);
      margin-bottom: 1.5rem;
      padding-bottom: 0.5rem;
      border-bottom: 2px solid #e8eaf6;
    }
    .form-row {
      display: flex;
      gap: 1.5rem;
      margin-bottom: 0.5rem;
    }
    .col {
      flex: 1;
    }
    .error-banner {
      background-color: #ffebee;
      color: #c62828;
      padding: 1rem;
      border-radius: 8px;
      margin-bottom: 1.5rem;
      border-left: 4px solid var(--error-color);
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .form-actions {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding-top: 1.5rem;
      border-top: 1px solid var(--border-color);
    }
    .spin {
      animation: spin 1s linear infinite;
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `]
})
export class UserCreateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private userService = inject(UserService);
  private roleService = inject(RoleService);
  private router = inject(Router);

  roles = signal<Role[]>([]);

  userForm = this.fb.group({
    username: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    password: ['', [Validators.required, Validators.minLength(6)]],
    role: ['', [Validators.required]],
    accessEnabled: [true]
  });

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  ngOnInit() {
    this.roleService.getAll().subscribe({
      next: (roles) => {
        this.roles.set(roles);
        // Set default role to first available if form is empty
        if (!this.userForm.get('role')?.value && roles.length > 0) {
          this.userForm.patchValue({ role: roles[0].name });
        }
      }
    });
  }

  onSubmit() {
    if (this.userForm.valid) {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      const formValue = this.userForm.value;
      // Normalize role payload to avoid nested objects (e.g. { name: { name: 'ROLE_ADMIN' } })
      const roleVal: unknown = formValue.role as unknown;
      let rolePayload: { name: string } | null = null;
      if (typeof roleVal === 'string') {
        rolePayload = { name: roleVal };
      } else if (roleVal && typeof roleVal === 'object') {
        const nameField: unknown = (roleVal as any).name;
        if (typeof nameField === 'string') {
          rolePayload = { name: nameField };
        } else if (nameField && typeof nameField === 'object' && typeof (nameField as any).name === 'string') {
          // Fix double-wrapped case
          rolePayload = { name: (nameField as any).name };
        }
      }

      const userData: any = {
        username: formValue.username,
        email: formValue.email,
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        password: formValue.password,
        accessEnabled: formValue.accessEnabled,
        role: rolePayload ?? { name: 'ROLE_CONTROLEUR' }
      };

      this.userService.createUser(userData).subscribe({
        next: () => {
          this.router.navigate(['/users']);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.errorMessage.set(err.error?.message || err.error || 'Une erreur est survenue lors de la création');
        }
      });
    }
  }
}
